/**
 * SeoFAQRenderer.tsx
 *
 * File purpose:
 * - Render visible FAQ blocks for pages that have faqs defined in the SEO audit dataset.
 * - The component is resilient to the pageSeoAudit module exporting either a named
 *   `pageSeoAudit` or a default export to avoid bundler resolution issues.
 *
 * Notes:
 * - Keeps UI non-intrusive: it only renders when faqs are present for the current page.
 */

import React, { useMemo } from 'react'
import { useLocation } from 'react-router'
import * as auditModule from '../seo/pageSeoAudit'
import FAQList from './faq/FAQList'

/**
 * Resolve the audit object from the imported module.
 * - Some versions export a named `pageSeoAudit`, others export a default object.
 */
const resolvedPageSeoAudit: Record<string, any> =
  (auditModule as any).pageSeoAudit ?? (auditModule as any).default ?? (auditModule as any)

/**
 * mapPathToAuditKey
 *
 * Purpose:
 * - Convert location.pathname into the audit key used by pageSeoAudit.
 *
 * Examples:
 * - '/' => 'home'
 * - '/services' => 'services'
 * - '/services/digital-marketing' => 'services/digital-marketing'
 *
 * @param path location.pathname
 * @returns audit key string
 */
function mapPathToAuditKey(path: string): string {
  const cleaned = path.replace(/^\/+|\/+$/g, '')
  return cleaned === '' ? 'home' : cleaned
}

/**
 * SeoFAQRenderer
 *
 * Purpose:
 * - Read current route and, if an entry in pageSeoAudit contains faqs, render FAQList.
 *
 * @returns JSX.Element | null
 */
export default function SeoFAQRenderer(): JSX.Element | null {
  const location = useLocation()

  const items = useMemo(() => {
    const key = mapPathToAuditKey(location.pathname)
    const entry = resolvedPageSeoAudit[key]
    if (!entry || !entry.faqs) return null
    return entry.faqs.map((f: any) => ({ question: f.question || '', answer: f.answer || '' }))
  }, [location.pathname])

  if (!items) return null

  return <FAQList items={items} />
}